import { useMemo, useState } from 'react';

import { DynamicFormContext } from '../context';
import {
  formElementMustBeShown,
  getAnswerValues,
  getSelectedOptionIds,
} from '../functions';
import { AnswerValues, DynamicFormState, FormEntry } from '../types';
import DynamicForm from './DynamicForm';

interface FormWithoutChaptersProps {
  formEntry: FormEntry;
  dynamicFormState: DynamicFormState;
  onSubmit: (answers: AnswerValues) => void;
  readOnly: boolean;
}

function FormWithoutChapters(props: FormWithoutChaptersProps) {
  const { filledOutFields } = props.formEntry;

  if (filledOutFields === undefined || filledOutFields.length === 0) {
    throw new Error('Form has no fields');
  }

  // keep track of selected option ids, so we can conditionally show/hide chapters and questions
  const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>(() =>
    getSelectedOptionIds(filledOutFields),
  );

  const [answerValues, setAnswerValues] = useState<AnswerValues>(() => {
    return getAnswerValues(filledOutFields);
  });

  const shownFieldIds: number[] = useMemo(
    () =>
      filledOutFields
        .filter((filledOutField) =>
          formElementMustBeShown(filledOutField.field, selectedOptionIds),
        )
        .map((filledOutField) => filledOutField.field.id),
    [filledOutFields, selectedOptionIds],
  );

  const value = useMemo(
    () => ({
      answerValues,
      setAnswerValues,
      setSelectedOptionIds,
      formIsReadOnly: props.readOnly,
    }),
    [answerValues, props.readOnly],
  );

  return (
    <DynamicFormContext.Provider value={value}>
      <DynamicForm
        state={props.dynamicFormState}
        filledOutFields={filledOutFields}
        shownFieldIds={shownFieldIds}
        onSubmit={() => props.onSubmit(answerValues)}
      />
    </DynamicFormContext.Provider>
  );
}

export default FormWithoutChapters;
